import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import ConfirmModal from './ConfirmModal';
import ApiErrorModal from './ApiErrorModal';
import CountryCodeSelect from './CountryCodeSelect';
import { validateAndFormatPhone } from '../utils/phoneValidation';
import { detectUserLocationData } from '../utils/geo';

export default function PhoneManager({ userId, shouldFetch = true, onLoaded, onRequestVerify, refreshTrigger }) {
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isAdding, setIsAdding] = useState(false);
  
  const handleAddClick = async () => {
    setIsAdding(true);
    setFormError(null);
    const loc = await detectUserLocationData();
    if (loc?.iso2) {
      setNewPhone(prev => ({ ...prev, countryCode: loc.iso2 }));
    }
  };
  const [editingId, setEditingId] = useState(null);
  
  const [newPhone, setNewPhone] = useState({ countryCode: 'US', number: '', type: 'mobile' });
  const [editPhoneVal, setEditPhoneVal] = useState({ countryCode: 'US', number: '', type: 'mobile' });
  const [saving, setSaving] = useState(false);
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: "", message: "" });
  const [formError, setFormError] = useState(null);
  
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const isBusy = saving || isAdding || editingId !== null;
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (shouldFetch) {
      fetchPhones();
    }
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [shouldFetch, userId]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchPhones();
    }
  }, [refreshTrigger]);

  const fetchPhones = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    setLoading(true);
    try {
      const res = await api.get('/users/me/phones', { signal: abortControllerRef.current.signal });
      setPhones(res.data?.phones || []);
      setError(null);
      if (onLoaded) onLoaded();
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error(err);
      setError('Failed to load phones.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newPhone.number.trim()) return;
    if (!validateAndFormatPhone(newPhone.number, newPhone.countryCode)) {
      setFormError('Invalid phone number format for selected country.');
      return;
    }
    setSaving(true);
    try {
      const formatted = validateAndFormatPhone(newPhone.number, newPhone.countryCode);
      const res = await api.post('/users/me/identities/phone', { phone: formatted, type: newPhone.type });
      if (res.data?.phone) {
        setPhones([...phones, res.data.phone]);
      } else {
        await fetchPhones();
      }
      setIsAdding(false);
      setNewPhone({ countryCode: 'US', number: '', type: 'mobile' });
    } catch (err) {
      setErrorModal({ isOpen: true, title: "Error", message: err.response?.data?.message || 'Failed to add phone' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editPhoneVal.number.trim()) return;
    if (!validateAndFormatPhone(editPhoneVal.number, editPhoneVal.countryCode)) {
      setFormError('Invalid phone number format for selected country.');
      return;
    }
    setSaving(true);
    try {
      const formatted = validateAndFormatPhone(editPhoneVal.number, editPhoneVal.countryCode);
      const res = await api.put(`/users/me/phones/${id}`, { phone: formatted, type: editPhoneVal.type });
      setPhones(phones.map(p => p.id === id ? res.data.phone : p));
      setFormError(null); setEditingId(null);
    } catch (err) {
      setErrorModal({ isOpen: true, title: "Error", message: err.response?.data?.message || 'Failed to update phone' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    setSaving(true);
    try {
      await api.delete(`/users/me/phones/${rowToDelete}`);
      await fetchPhones();
      setShowConfirm(false);
      setRowToDelete(null);
    } catch (err) {
      setErrorModal({ isOpen: true, title: "Error", message: err.response?.data?.message || 'Failed to delete phone' });
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimary = async (id) => {
    setSaving(true);
    try {
      await api.put(`/users/me/phones/${id}/primary`);
      await fetchPhones();
    } catch (err) {
      setErrorModal({ isOpen: true, title: "Error", message: err.response?.data?.message || 'Failed to set primary phone' });
    } finally {
      setSaving(false);
    }
  };

  if (loading && phones.length === 0) {
    return <div className="text-sm text-slate-500 animate-pulse">Loading phones...</div>;
  }

  const renderForm = (val, setVal, onSave, onCancel, saveText) => (
    <div className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-lg border border-slate-200 dark:border-slate-700 flex flex-col gap-3 mt-2">
      {formError && <div className="p-2.5 bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-xs rounded-lg border border-red-200 dark:border-red-800">{formError}</div>}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="w-full sm:w-24">
           <CountryCodeSelect value={val.countryCode} onChange={(c) => setVal({...val, countryCode: c.iso2})} preload={true} />
        </div>
        <input type="tel" value={val.number} onChange={(ev) => setVal({...val, number: ev.target.value})} className="flex-1 w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-3 py-1.5 text-sm" placeholder="Phone number" />
        <select value={val.type} onChange={(ev) => setVal({...val, type: ev.target.value})} className="w-full sm:w-28 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 text-sm">
          <option value="mobile">Mobile</option>
          <option value="home">Home</option>
          <option value="work">Work</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div className="flex items-center gap-2 justify-end">
        <button type="button" onClick={onCancel} disabled={saving} className="px-3 py-1.5 text-xs text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 transition">Cancel</button>
        <button type="button" onClick={onSave} disabled={saving} className="px-3 py-1.5 text-xs text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50 transition shadow-sm">{saving ? 'Saving...' : saveText}</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Phone Numbers</h3>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchPhones} disabled={isBusy || loading} className={`p-1 text-slate-400 hover:text-indigo-600 transition-colors disabled:opacity-50 ${isBusy ? 'hidden' : ''}`}>
             <svg className={`w-4 h-4 ${loading ? 'animate-spin text-indigo-600' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </button>
          {!isBusy && (
            <button type="button" onClick={handleAddClick} className="text-xs bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-lg font-medium transition-colors shadow-sm disabled:opacity-50">
              + Add Phone
            </button>
          )}
        </div>
      </div>
      
      {error && <div className="text-xs text-red-600">{error}</div>}

      <div className="space-y-2">
        {phones.map(p => {
          if (editingId === p.id) {
            return (
              <React.Fragment key={p.id}>
                {renderForm(editPhoneVal, setEditPhoneVal, () => handleUpdate(p.id), () => { setFormError(null); setEditingId(null); }, 'Save')}
              </React.Fragment>
            );
          }

          return (
            <div key={p.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <label className={`cursor-pointer flex items-center ${isBusy ? 'opacity-50 pointer-events-none' : ''}`}>
                  <input type="radio" name="primaryPhoneRadio" checked={p.isPrimary || p.is_primary} onChange={() => handleSetPrimary(p.id)} disabled={isBusy} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:opacity-50" />
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-900 dark:text-white capitalize">{p.type}:</span>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">{String(p.countryCode || p.country_code || "").startsWith("+") ? "" : "+"}{p.countryCode || p.country_code} {p.number}</span>
                  {(p.isPrimary || p.is_primary) && <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">Primary</span>}
                  {(p.isVerified || p.is_verified) ? (
                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">Verified</span>
                  ) : (
                    <button type="button" onClick={() => onRequestVerify && onRequestVerify('phone', '+' + (p.countryCode || p.country_code).replace('+', '') + p.number)} className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 hover:bg-amber-200 transition-colors text-amber-800 border border-amber-300">Unverified (Click to Verify)</button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => { setEditPhoneVal({ countryCode: p.countryCode || p.country_code, number: p.number, type: p.type }); setFormError(null); setEditingId(p.id); }} disabled={isBusy || (p.isVerified || p.is_verified)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition-colors disabled:opacity-40" title={(p.isVerified || p.is_verified) ? "Verified phones cannot be edited" : "Edit phone"}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
                </button>
                <button type="button" onClick={() => { setRowToDelete(p.id); setShowConfirm(true); }} disabled={isBusy} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors disabled:opacity-40" title="Delete phone">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                </button>
              </div>
            </div>
          );
        })}

        {phones.length === 0 && !isAdding && (
          <p className="text-sm text-slate-500 italic p-4 bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">No phone numbers added yet.</p>
        )}
      </div>

      {isAdding && renderForm(newPhone, setNewPhone, handleAdd, () => setIsAdding(false), 'Add Phone')}

      <ConfirmModal
        isOpen={showConfirm}
        title="Delete Phone Number"
        message="Are you sure you want to delete this phone number? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowConfirm(false)}
        isDestructive={true}
      />
      <ApiErrorModal
        isOpen={errorModal.isOpen}
        title={errorModal.title}
        message={errorModal.message}
        onCancel={() => setErrorModal(prev => ({ ...prev, isOpen: false }))}
        onReload={() => setErrorModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
